import { useCallback } from 'react';
import { useAppSelector } from '../../app/hooks';
import { getLevel } from '../../utils/visibilityConfigUtils';
import type { VisibilityConfigMap } from '../../utils/visibilityConfigUtils';
import { VISIBILITY_LEVEL } from '../../utils/constants/visibilityKeys';
import { PNO_HEADER_BAND_PX } from '../PlateEditor/exporters/pageGeometry';
import { fetchImageAsDataUrl } from './fetchImageAsDataUrl';

/** Visibility-config key that turns the PNO logo on for report PDF export. */
export const REPORTS_LOGO_FLAG = 'reports.logo.enabled';

/**
 * Visibility-config key that toggles the EDITOR's own painted preview of the
 * PNO logo band (RECUR-27604 editor-band flag) — a kill switch layered on
 * top of `REPORTS_LOGO_FLAG`, never a substitute for it: it is read ONLY by
 * `usePnoReportLogoBand`, never by `usePnoReportLogoResolver`, so the PDF
 * export path is structurally unaffected by this key. ABSENT or explicit
 * WRITE(1) keeps the editor band active (default-on — a PNO that already has
 * the PDF logo enabled should see the same chrome while editing); an
 * EXPLICIT READ(0) turns the editor's own band fully off (no bands, no
 * masks, no display-gap addend at a seam or on page 1, no gated row-offset
 * correction) while the model's own `reservedPageTopPx` reservation stays
 * active so editor seams keep matching the PDF. `getLevel` alone can't
 * express "absent differs from explicit READ" (both fail-open to the SAME
 * `VISIBILITY_LEVEL.READ` default) — the raw-presence check below is what
 * distinguishes them; everything else reuses the SAME `getLevel`/
 * `VISIBILITY_LEVEL` machinery `REPORTS_LOGO_FLAG` itself uses.
 */
export const REPORTS_LOGO_EDITOR_BAND_FLAG = 'reports.logo.editorBand';

/** A stalled logo endpoint must never hold the export click open indefinitely. */
export const LOGO_FETCH_TIMEOUT_MS = 5_000;

type RawVisibilityConfig = { additionalDetailsConfig?: VisibilityConfigMap } | VisibilityConfigMap | null;

/**
 * The non-obvious `additionalDetailsConfig` unwrap, extracted so THIS file's
 * two flag reads (`REPORTS_LOGO_FLAG` below, `REPORTS_LOGO_EDITOR_BAND_FLAG`
 * in `usePnoReportLogoBand`) share one copy instead of a second inline
 * duplicate — other files in this codebase (`useVisibilityConfig.ts`,
 * `useNotificationBellEnabled.ts`) each keep their own local copy of the same
 * one-liner, so this stays file-local rather than becoming a new shared util.
 */
function unwrapVisibilityConfig(raw: RawVisibilityConfig): VisibilityConfigMap {
    return ((raw as { additionalDetailsConfig?: VisibilityConfigMap })?.additionalDetailsConfig ?? raw) as VisibilityConfigMap;
}

/**
 * The PNO logo's CONFIG state — flag + configured URL, no fetch. Extracted so
 * both `usePnoReportLogoResolver` (fetches on demand) and
 * `usePnoReportLogoBand` (config-only, no network — the editor overlay must
 * decide on every render pass without waiting on a fetch) read the SAME
 * visibility-config unwrap exactly once, rather than duplicating the
 * non-obvious `additionalDetailsConfig` unwrap below.
 */
const usePnoLogoConfig = (): { enabled: boolean; pnoLogoUrl?: string } => {
    // Read the already-loaded visibility config straight from the store (the app's
    // own useVisibilityConfig usage owns the fetch — we only read), so this hook
    // adds no router/dispatch dependency to its consumers.
    const enabled = useAppSelector((state) => {
        const raw = state.user?.visibilityConfig as RawVisibilityConfig;
        return getLevel(unwrapVisibilityConfig(raw), REPORTS_LOGO_FLAG) === VISIBILITY_LEVEL.WRITE;
    });
    const pnoLogoUrl = useAppSelector((state) => (state.user?.domainMetadata as { logoUrl?: string } | null)?.logoUrl);
    return { enabled, pnoLogoUrl };
};

/**
 * Returns an async resolver for the PNO logo on report PDF export. Call it at
 * export time; it yields a `data:` URI ONLY when the PNO's visibility config sets
 * `reports.logo.enabled` to 1 AND a logo is configured — otherwise `undefined`
 * (→ export with no logo). `getLevel` fails OPEN to READ(0) for an absent key, so
 * we require exactly WRITE(1): opt-in, off by default. Resolving on demand (not
 * via render state) avoids a race with auto-on-mount exporters (HiddenPdfExporter).
 */
export const usePnoReportLogoResolver = (): (() => Promise<string | undefined>) => {
    const { enabled, pnoLogoUrl } = usePnoLogoConfig();

    return useCallback(async () => {
        if (!enabled || !pnoLogoUrl) return undefined;
        try {
            return await fetchImageAsDataUrl(pnoLogoUrl, LOGO_FETCH_TIMEOUT_MS);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('usePnoReportLogo: could not load PNO logo for report export', err);
            return undefined;
        }
    }, [enabled, pnoLogoUrl]);
};

/**
 * The per-page vertical space (CSS content px) the editor's page-boundary
 * overlay must reserve, PAIRED with the plain logo URL it paints there
 * (RECUR-27604 phase 4) — CONFIG-only, no fetch, so the overlay can decide on
 * every measurement pass without waiting on network. `reservedPx`/`logoUrl`
 * are `0`/`undefined` together whenever the export itself would end up
 * logo-free (flag off, or no URL configured) — REPLACES the old,
 * reservation-only `usePnoReportLogoReservedPx` now that the overlay also
 * needs the URL to paint the band itself, rather than adding a second
 * near-duplicate hook.
 *
 * `editorBandEnabled` (RECUR-27604 editor-band flag) is a SEPARATE kill
 * switch on top of `reservedPx`/`logoUrl`: it governs only whether the editor
 * PAINTS its own preview of the band (see `REPORTS_LOGO_EDITOR_BAND_FLAG`'s
 * doc comment) — `reservedPx` keeps reporting the full `PNO_HEADER_BAND_PX`
 * reservation even when `editorBandEnabled` is false, so
 * `usePageBoundaries`' page-CAPACITY math (and therefore every editor seam's
 * agreement with the actual PDF break) is completely unaffected by this flag.
 *
 * Accepted divergence (documented, not fixed here): the overlay reserves on
 * *config* while the export drops the band if the *fetch* fails at click
 * time — see the plan's §2.6 for why closing that gap (a module-level
 * failure cache, or reserving space even on fetch failure) was rejected as
 * disproportionate to the bounded impact (one band's worth of seam
 * disagreement, only on an already-warning broken-endpoint path).
 */
export const usePnoReportLogoBand = (): { reservedPx: number; logoUrl?: string; editorBandEnabled: boolean } => {
    const { enabled, pnoLogoUrl } = usePnoLogoConfig();
    // Read independently of `usePnoLogoConfig` (never folded into it) so
    // `usePnoReportLogoResolver` — the PDF export path — never touches this
    // key at all; that is what makes "PDF resolver behaviour is provably
    // unaffected by the new key" true by construction, not just by test.
    const editorBandEnabled = useAppSelector((state) => {
        const config = unwrapVisibilityConfig(state.user?.visibilityConfig as RawVisibilityConfig);
        // `getLevel` fails OPEN to READ(0) for BOTH an absent key and an
        // explicit READ(0) — indistinguishable from its return value alone.
        // This flag needs the opposite default (absent ⇒ active), so presence
        // is checked directly on the raw config before handing an EXPLICIT
        // value to the same getLevel/VISIBILITY_LEVEL machinery
        // REPORTS_LOGO_FLAG itself uses.
        if (!config || typeof config !== 'object' || config[REPORTS_LOGO_EDITOR_BAND_FLAG] === undefined) return true;
        return getLevel(config, REPORTS_LOGO_EDITOR_BAND_FLAG) === VISIBILITY_LEVEL.WRITE;
    });
    return enabled && pnoLogoUrl
        ? { reservedPx: PNO_HEADER_BAND_PX, logoUrl: pnoLogoUrl, editorBandEnabled }
        : { reservedPx: 0, logoUrl: undefined, editorBandEnabled };
};
